import { Store } from 'components/common/types/Store.types';
import { TargetType } from 'components/pages/Planner/PlannerSections/TargetSection/TargetSection.types';
import { UseTarget } from 'components/pages/Planner/PlannerSections/components/TargetsPanel/TargetsPanel.types';
import { TargetLabel } from 'consts/targetLabel';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPublisherTargetCriteria } from 'store/publisher/selectors';
import { hideSecondaryPanel } from 'store/productManagement/reducer';
import frameSvg from 'assets/icons/frame.svg';
import eyeSvg from 'assets/icons/eye_empty.svg';
import { getEffectiveCriteria } from 'utils/getPublisherDefaultCriteria';
import { definedRangePermissionOptions as permissionOptions } from '../../consts';
import { secondaryPanelLayoutStyles } from '../../styles';
import { ProductTargetsPanelProps } from './ProductTargetsPanel.types';
import { ProductTargetSection } from './ProductTargetSection';
import { getTargetData, isSaveDisabled } from './utils';
import { getRangeErrors } from '../../utils/validateRangeValues';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

// eslint-disable-next-line sonarjs/cognitive-complexity
const ProductTargetsPanel: React.FC<ProductTargetsPanelProps> = ({ onConfirm, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const budget = useSelector((state: Store) => state.productManagement.newProduct?.budget);
  const frames = useSelector((state: Store) => state.productManagement.newProduct?.frames);
  const impressions = useSelector((state: Store) => state.productManagement.newProduct?.impressions);

  const publisherCriteria = useSelector(getPublisherTargetCriteria);

  const budgetCriteria = getEffectiveCriteria(budget, publisherCriteria);
  const framesCriteria = getEffectiveCriteria(frames, publisherCriteria);
  const impressionsCriteria = getEffectiveCriteria(impressions, publisherCriteria);

  const defaultSelectedBudgetPermission =
    permissionOptions.find(({ code }) => code === budget?.selectionPermission) ?? permissionOptions[0];
  const defaultSelectedFramesPermission =
    permissionOptions.find(({ code }) => code === frames?.selectionPermission) ?? permissionOptions[0];
  const defaultSelectedImpressionsPermission =
    permissionOptions.find(({ code }) => code === impressions?.selectionPermission) ?? permissionOptions[0];

  const [selectedBudgetPermission, setSelectedBudgetPermission] = useState(defaultSelectedBudgetPermission);

  const [budgetValue, setBudgetValue] = useState(budget?.value);
  const [budgetGreenTolerance, setBudgetGreenTolerance] = useState(budgetCriteria.greenTolerance);
  const [budgetOrangeTolerance, setBudgetOrangeTolerance] = useState(budgetCriteria.orangeTolerance);
  const [budgetPriority, setBudgetPriority] = useState(budgetCriteria.priority);
  const [budgetMinValue, setBudgetMinValue] = useState(budget?.minValue);
  const [budgetMaxValue, setBudgetMaxValue] = useState(budget?.maxValue);
  const [budgetDefaultValue, setBudgetDefaultValue] = useState(budget?.defaultValue);

  const [selectedFramesPermission, setSelectedFramesPermission] = useState(defaultSelectedFramesPermission);

  const [framesValue, setFramesValue] = useState(frames?.value);
  const [framesGreenTolerance, setFramesGreenTolerance] = useState(framesCriteria.greenTolerance);
  const [framesOrangeTolerance, setFramesOrangeTolerance] = useState(framesCriteria.orangeTolerance);
  const [framesPriority, setFramesPriority] = useState(framesCriteria.priority);
  const [framesMinValue, setFramesMinValue] = useState(frames?.minValue);
  const [framesMaxValue, setFramesMaxValue] = useState(frames?.maxValue);
  const [framesDefaultValue, setFramesDefaultValue] = useState(frames?.defaultValue);

  const [selectedImpressionsPermission, setSelectedImpressionsPermission] = useState(
    defaultSelectedImpressionsPermission,
  );

  const [impressionsValue, setImpressionsValue] = useState(impressions?.value);
  const [impressionsGreenTolerance, setImpressionsGreenTolerance] = useState(impressionsCriteria.greenTolerance);
  const [impressionsOrangeTolerance, setImpressionsOrangeTolerance] = useState(impressionsCriteria.orangeTolerance);
  const [impressionsPriority, setImpressionsPriority] = useState(impressionsCriteria.priority);
  const [impressionsMinValue, setImpressionsMinValue] = useState(impressions?.minValue);
  const [impressionsMaxValue, setImpressionsMaxValue] = useState(impressions?.maxValue);
  const [impressionsDefaultValue, setImpressionsDefaultValue] = useState(impressions?.defaultValue);

  const budgetTargetErrors = getRangeErrors(budgetDefaultValue, budgetMinValue, budgetMaxValue);
  const framesTargetErrors = getRangeErrors(framesDefaultValue, framesMinValue, framesMaxValue);
  const impressionsTargetErrors = getRangeErrors(impressionsDefaultValue, impressionsMinValue, impressionsMaxValue);

  const budgetTarget: UseTarget = {
    value: budgetValue,
    priority: budgetPriority,
    greenTolerance: budgetGreenTolerance,
    orangeTolerance: budgetOrangeTolerance,
    handleValueChange: setBudgetValue,
    handlePriorityChange: setBudgetPriority,
    handleGreenToleranceChange: setBudgetGreenTolerance,
    handleOrangeToleranceChange: setBudgetOrangeTolerance,
    handleMinValueChange: setBudgetMinValue,
    handleMaxValueChange: setBudgetMaxValue,
    handleDefaultValueChange: setBudgetDefaultValue,
    isToleranceError: false,
    isChange: false,
    minValue: budgetMinValue,
    maxValue: budgetMaxValue,
    defaultValue: budgetDefaultValue,
  };

  const framesTarget: UseTarget = {
    value: framesValue,
    priority: framesPriority,
    greenTolerance: framesGreenTolerance,
    orangeTolerance: framesOrangeTolerance,
    handleValueChange: setFramesValue,
    handlePriorityChange: setFramesPriority,
    handleGreenToleranceChange: setFramesGreenTolerance,
    handleOrangeToleranceChange: setFramesOrangeTolerance,
    handleMinValueChange: setFramesMinValue,
    handleMaxValueChange: setFramesMaxValue,
    handleDefaultValueChange: setFramesDefaultValue,
    isToleranceError: false,
    isChange: false,
    minValue: framesMinValue,
    maxValue: framesMaxValue,
    defaultValue: framesDefaultValue,
  };

  const impressionsTarget: UseTarget = {
    value: impressionsValue,
    priority: impressionsPriority,
    greenTolerance: impressionsGreenTolerance,
    orangeTolerance: impressionsOrangeTolerance,
    handleValueChange: setImpressionsValue,
    handlePriorityChange: setImpressionsPriority,
    handleGreenToleranceChange: setImpressionsGreenTolerance,
    handleOrangeToleranceChange: setImpressionsOrangeTolerance,
    handleMinValueChange: setImpressionsMinValue,
    handleMaxValueChange: setImpressionsMaxValue,
    handleDefaultValueChange: setImpressionsDefaultValue,
    isToleranceError: false,
    isChange: false,
    minValue: impressionsMinValue,
    maxValue: impressionsMaxValue,
    defaultValue: impressionsDefaultValue,
  };

  const handleSubmit = (): void => {
    onConfirm({
      budget: getTargetData({
        permission: selectedBudgetPermission.code,
        value: budgetValue,
        priority: budgetPriority,
        greenTolerance: budgetGreenTolerance,
        orangeTolerance: budgetOrangeTolerance,
        minValue: budgetMinValue,
        maxValue: budgetMaxValue,
        defaultValue: budgetDefaultValue,
      }),
      impressions: getTargetData({
        permission: selectedImpressionsPermission.code,
        value: impressionsValue,
        priority: impressionsPriority,
        greenTolerance: impressionsGreenTolerance,
        orangeTolerance: impressionsOrangeTolerance,
        minValue: impressionsMinValue,
        maxValue: impressionsMaxValue,
        defaultValue: impressionsDefaultValue,
      }),
      frames: getTargetData({
        permission: selectedFramesPermission.code,
        value: framesValue,
        priority: framesPriority,
        greenTolerance: framesGreenTolerance,
        orangeTolerance: framesOrangeTolerance,
        minValue: framesMinValue,
        maxValue: framesMaxValue,
        defaultValue: framesDefaultValue,
      }),
    });
  };

  const isSaveButtonDisabled =
    isReadOnly ||
    isSaveDisabled({
      budget: {
        value: budgetValue,
        minValue: budgetMinValue,
        maxValue: budgetMaxValue,
      },
      frames: {
        value: framesValue,
        minValue: framesMinValue,
        maxValue: framesMaxValue,
      },
      impressions: {
        value: impressionsValue,
        minValue: impressionsMinValue,
        maxValue: impressionsMaxValue,
      },
      selectedBudgetPermission: selectedBudgetPermission.code,
      selectedFramesPermission: selectedFramesPermission.code,
      selectedImpressionsPermission: selectedImpressionsPermission.code,
    }) ||
    budgetTargetErrors.isAnyTargetValueError ||
    framesTargetErrors.isAnyTargetValueError ||
    impressionsTargetErrors.isAnyTargetValueError;

  const hasTargetsChanges =
    selectedBudgetPermission.code !== budget?.selectionPermission ||
    selectedFramesPermission.code !== frames?.selectionPermission ||
    selectedImpressionsPermission.code !== impressions?.selectionPermission;

  return (
    <ProductSecondaryPanel
      size="lg"
      title="Target"
      onSubmit={handleSubmit}
      onCancel={() => dispatch(hideSecondaryPanel())}
      customStyles={secondaryPanelLayoutStyles}
      isDisabled={isSaveButtonDisabled}
      isChange={hasTargetsChanges}
    >
      <ProductTargetSection
        dataTestId="product-budget-selection-permission"
        label={TargetLabel.BUDGET}
        permissionSetter={setSelectedBudgetPermission}
        selectedPermission={selectedBudgetPermission}
        target={budgetTarget}
        targetType={TargetType.BUDGET}
        title="Budget selection permissions"
      />
      <ProductTargetSection
        dataTestId="product-frames-selection-permission"
        icon={frameSvg}
        label={TargetLabel.FRAMES}
        permissionSetter={setSelectedFramesPermission}
        selectedPermission={selectedFramesPermission}
        target={framesTarget}
        targetType={TargetType.FRAMES}
        title="Frames selection permissions"
      />
      <ProductTargetSection
        dataTestId="product-impressions-selection-permission"
        icon={eyeSvg}
        label={TargetLabel.IMPRESSIONS}
        permissionSetter={setSelectedImpressionsPermission}
        selectedPermission={selectedImpressionsPermission}
        target={impressionsTarget}
        targetType={TargetType.IMPRESSIONS}
        title="Impressions selection permissions"
      />
    </ProductSecondaryPanel>
  );
};

export default ProductTargetsPanel;
